import { useFeatureGate } from "@statsig/react-bindings";
import { createContext, useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";

import { Scenario } from "../../api/getScenario";
import { useGeoEnabled } from "../../hooks/useGeoEnabled";
import {
  ScenarioResultRevenue,
  ScenarioResultSpend,
  useScenarioGeographyData,
} from "../../hooks/useScenarioGeographyData";
import { useScenarioV2 } from "../../hooks/useScenarioV2";
import { bcpLayerKey, BCPNode } from "../../utils";

const ScenarioResultsContext = createContext<{
  scenario: Scenario | undefined;
  revenues: ScenarioResultRevenue[];
  spends: ScenarioResultSpend[];
  availableGeographies: string[];
  selectedGeography: string | null;
  setSelectedGeography: (geography: string | null) => void;
  isScenarioLoading: boolean;
  bcpKeyToNode: Record<string, BCPNode> | undefined;
} | null>(null);

export const ScenarioResultsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { uuid } = useParams<{ uuid: string }>();
  const [selectedGeography, setSelectedGeography] = useState<string | null>(null);
  const geoEnabledQuery = useGeoEnabled();
  const isGeoEnabled = geoEnabledQuery.isLoading ? false : geoEnabledQuery.isEnabled;
  const { value: isScenariosApiEnabled } = useFeatureGate(featureFlag.scenarioBuilderApi);

  const { scenario, isScenarioLoading } = useScenarioV2({
    uuid,
    isScenariosApiEnabled,
    isGeoEnabled,
  });

  // Use geography data hook to filter/aggregate data based on selection
  const { revenues, spends, availableGeographies } = useScenarioGeographyData({
    revenues: scenario?.scenarioReport?.scenarioRevenues || [],
    spends: scenario?.scenarioReport?.scenarioSpends || [],
    selectedGeography,
  });

  const bcpKeyToNode = useMemo(() => {
    return scenario?.scenarioConstraints?.reduce(
      (acc, constraint) => {
        const key = bcpLayerKey({
          brand: constraint.brand ?? "",
          category: constraint.category ?? "",
          product: constraint.product ?? "",
        });
        acc[key] = {
          brand: constraint.brand ?? "",
          category: constraint.category ?? "",
          product: constraint.product ?? "",
        };
        return acc;
      },
      {} as Record<string, BCPNode>
    );
  }, [scenario?.scenarioConstraints]);

  return (
    <ScenarioResultsContext.Provider
      value={{
        scenario,
        revenues,
        spends,
        availableGeographies,
        selectedGeography,
        setSelectedGeography,
        isScenarioLoading,
        bcpKeyToNode,
      }}
    >
      {children}
    </ScenarioResultsContext.Provider>
  );
};

export const useScenarioResultsContext = () => {
  const context = useContext(ScenarioResultsContext);
  if (!context) {
    throw new Error("useScenarioForms must be used within a ScenarioFormProvider");
  }
  return context;
};
